import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const CategoryContext = createContext({});

export const useCategories = () => useContext(CategoryContext);

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api';

const STORAGE_KEY = 'app_categories';
const CACHE_TTL = 10 * 60 * 1000; // 10 minutes

const getCache = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Date.now() - parsed.timestamp > CACHE_TTL) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    sessionStorage.removeItem(STORAGE_KEY);
    return null;
  }
};

const setCache = (flat, tree) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
      flat,
      tree,
      timestamp: Date.now(),
    }));
  } catch { /* storage full – ignore */ }
};

const flattenTree = (nodes) => {
  let result = [];
  for (const node of nodes) {
    result.push(node);
    if (node.children) result = result.concat(flattenTree(node.children));
  }
  return result;
};

export const CategoryProvider = ({ children }) => {
  const [categories, setCategories] = useState([]);     // flat active list
  const [categoryTree, setCategoryTree] = useState([]);  // hierarchical tree
  const [loading, setLoading] = useState(true);

  const fetchCategories = useCallback(async (bypassCache = false) => {
    if (!bypassCache) {
      const cached = getCache();
      if (cached) {
        setCategories(cached.flat);
        setCategoryTree(cached.tree);
        setLoading(false);
        return;
      }
    }

    try {
      setLoading(true);
      const [flatRes, treeRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/products/categories`),
        axios.get(`${API_BASE_URL}/products/categories-tree`),
      ]);

      const activeFlat = flatRes.data.filter(cat => cat.is_active);
      const tree = treeRes.data;

      setCategories(activeFlat);
      setCategoryTree(tree);
      setCache(activeFlat, tree);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const refreshCategories = useCallback(() => {
    sessionStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem('navbar_categories');
    return fetchCategories(true);
  }, [fetchCategories]);

  const getCategoryById = useCallback((id) => {
    return categories.find(c => c.id === id);
  }, [categories]);

  const getCategoryName = useCallback((id) => {
    const cat = categories.find(c => c.id === id);
    return cat ? cat.name : 'Unknown';
  }, [categories]);

  const flattenedTree = React.useMemo(() => flattenTree(categoryTree), [categoryTree]);

  const value = {
    categories,
    categoryTree,
    flattenedTree,
    loading,
    refreshCategories,
    getCategoryById,
    getCategoryName,
  };

  return (
    <CategoryContext.Provider value={value}>
      {children}
    </CategoryContext.Provider>
  );
};

export default CategoryContext;
